import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../../api/admin/adminApi';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import '../../styles/AdminDashboard.css';

const cardStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: '14px 16px',
  background: 'var(--vx-admin-surface)',
  border: '1px solid var(--vx-admin-border)',
  borderRadius: 12,
  color: 'var(--vx-admin-text)',
};

function formatWhen(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function shortId(value) {
  if (!value) return '—';
  const s = String(value);
  return s.length <= 10 ? s : `…${s.slice(-8)}`;
}

function actorLabel(event) {
  return event?.actorEmail || shortId(event?.actorId) || event?.actorType || '—';
}

function subjectLabel(event) {
  if (!event?.subjectType && !event?.subjectId) return '—';
  return [event.subjectType, shortId(event.subjectId)].filter(Boolean).join(' ');
}

function workspaceLabel(event) {
  return event?.enterpriseId ? shortId(event.enterpriseId) : '—';
}

export default function AdminAuditPage() {
  const { registerShellMeta, setRefreshHandler } = useOutletContext() || {};
  const isDesktop = useMediaQuery('(min-width: 769px)');
  const [action, setAction] = useState('');
  const [debouncedAction, setDebouncedAction] = useState('');
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedAction(action.trim()), 300);
    return () => clearTimeout(t);
  }, [action]);

  const load = useCallback(async (filter = debouncedAction) => {
    setError('');
    const res = await adminApi.listAuditEvents({
      action: filter || undefined,
      limit: 100,
    });
    setEvents(res.events || []);
  }, [debouncedAction]);

  useEffect(() => {
    registerShellMeta?.({
      title: 'Audit',
      subtitle: 'Privileged actions across companies, billing and staff',
    });
    setRefreshHandler?.(() => load(debouncedAction));
  }, [load, debouncedAction, registerShellMeta, setRefreshHandler]);

  useEffect(() => {
    setLoading(true);
    load(debouncedAction)
      .catch((err) => setError(err.message || 'Failed to load audit events'))
      .finally(() => setLoading(false));
  }, [load, debouncedAction]);

  return (
    <div className="admin-dashboard">
      <div className="analytics-section">
        <div className="admin-table-header">
          <div>
            <h2>Audit log</h2>
            <p className="admin-table-subtitle">
              Attributable staff and admin actions. Filter by action text, for example <code>staff.granted</code>.
            </p>
          </div>
          <div className="admin-table-actions">
            <button type="button" className="btn" onClick={() => load(debouncedAction).catch((err) => setError(err.message))}>
              Refresh
            </button>
          </div>
        </div>

        <div className="admin-table-filters">
          <input
            type="search"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="Filter action…"
            aria-label="Filter by action"
          />
        </div>

        {error ? <p className="error-message" role="alert">{error}</p> : null}
        {loading ? <p>Loading audit events…</p> : null}

        {isDesktop ? (
          <div className="admin-table">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Subject</th>
                  <th>Workspace</th>
                </tr>
              </thead>
              <tbody>
                {!loading && events.length === 0 ? (
                  <tr>
                    <td colSpan={5}>No audit events match.</td>
                  </tr>
                ) : (
                  events.map((event) => (
                    <tr key={event._id || `${event.action}-${event.at}-${event.subjectId}`}>
                      <td>{formatWhen(event.at)}</td>
                      <td>{actorLabel(event)}</td>
                      <td>{event.action || '—'}</td>
                      <td>{subjectLabel(event)}</td>
                      <td>{workspaceLabel(event)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {!loading && events.length === 0 ? <p className="admin-table-subtitle">No audit events match.</p> : null}
            {events.map((event) => (
              <div key={event._id || `${event.action}-${event.at}-${event.subjectId}`} style={cardStyle}>
                <strong>{event.action || '—'}</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px', fontSize: 12, color: 'var(--vx-admin-muted)' }}>
                  <span>{formatWhen(event.at)}</span>
                  <span>{actorLabel(event)}</span>
                  <span>{subjectLabel(event)}</span>
                  <span>Workspace {workspaceLabel(event)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
